import React, {useEffect, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {Formik} from 'formik';
import moment from 'moment/moment';
import {useDispatch} from 'react-redux';
import {useInspectionsPageUIContext} from '../InspectionsPageUIContext';
import {useCitiesContext, useStreetsContext} from '../../../_common/components/CitiesAndStreets';
import ButtonDD from '../../../_common/components/ButtonDD';
import ClearIcon from '../../../_common/components/ClearIcon';
import FilterText from '../../../_common/components/FilterText';
import FilterSelect from '../../../_common/components/FilterSelect';
import ButtonAction from '../../../_common/components/ButtonAction';
import {FilterDatePicker} from '../../../_common/components/FieldDatepicker';
import {generateExcelFile} from '../../../_common/utils/excel';
import {applyFilter} from '../../../../../utils/prepareApplyFilter';
import {getEstimatePrice} from '../../utils/getEstimatePrice';
import {getPaidPartValue} from '../../utils/getPaidPartValue';
import {getElevator} from '../../utils/getElevator';
import {toAbsoluteUrl} from '../../../../../_metronic/_helpers';
import {useDecisionTreeOptionsContext} from '../../../_common/components/DecisionTreeOptions';
import {getInspectionsListForExcel} from '../../_redux/actions/InspectionsPageTableActions';
import {shumaStatuses} from '../../../_common/constants/options';
import {columnNamesInspections} from '../../../_common/constants/excelColumnNames';
import customStyles from './styles.module.scss';
import styles from '../../../_common/styles/filtersBar.module.scss';

export function InspectionsPageFilters({users, shumaTypes}) {
  const {formatMessage} = useIntl();
  const dispatch = useDispatch();
  const inspectionsContext = useInspectionsPageUIContext();
  const inspectionsProps = useMemo(() => {
    return {
      queryParams: inspectionsContext.queryParams,
      setQueryParams: inspectionsContext.setQueryParams,
    };
  }, [inspectionsContext]);
  const {cities} = useCitiesContext();
  const {streets, updateStreets} = useStreetsContext();
  const {propertyTypes} = useDecisionTreeOptionsContext();
  const elevatorPresence = [
    {
      value: '0',
      label: formatMessage({
        id: 'inspections-page-filter-elevator-absent',
        defaultMessage: 'Absent',
      })
    },
    {
      value: '1',
      label: formatMessage({
        id: 'inspections-page-filter-elevator-got',
        defaultMessage: 'Elevator',
      }),
    },
  ];
  const [loading, setLoading] = useState(false);

  const qs = new URLSearchParams(window.location.search);
  // main
  let cityID = qs.get('cityID') || '';
  let streetID = qs.get('streetID') || '';
  let gush = qs.get('gush') || '';
  let helka = qs.get('helka') || '';
  let shumaID = qs.get('shumaID') || '';
  let shumaTypeID = qs.get('shumaTypeID') || '';
  let responsibleUserID = qs.get('responsibleUserID') || '';
  let statusTypeID = qs.get('statusTypeID') || '';
  // advanced
  let toDate = qs.get('toDate') || '';
  let fromDate = qs.get('fromDate') || '';
  let propertyTypeID = qs.get('propertyTypeID') || '';
  let elevator = qs.get('elevator') || '';
  let fromRoomCount = qs.get('fromRoomCount') || '';
  let toRoomCount = qs.get('toRoomCount') || '';
  let fromRegisteredArea = qs.get('fromRegisteredArea') || '';
  let toRegisteredArea = qs.get('toRegisteredArea') || '';
  let customerName = qs.get('customerName') || '';
  let fromShumaDueDate = qs.get('fromShumaDueDate') || '';
  let toShumaDueDate = qs.get('toShumaDueDate') || '';

  useEffect(() => {
    if (cityID) {
      updateStreets(cityID);
    }
  }, []);
  const handleCityChange = values => selected => {
    const {cityID} = values;
    if (!cityID || cityID !== selected.value) {
      updateStreets(selected.value);
    }
  };
  const prefix = 'filter-placeholder';

  const handleExport = () => {
    setLoading(true);
    dispatch(getInspectionsListForExcel(inspectionsProps.queryParams))
      .then(prepareDataForExcel)
      .then((data) => {
        generateExcelFile(
          data,
          'Inspections.xlsx',
          'דוחות המשרד'
        );
      })
      .catch((error) => {
        console.error(`Error: + ${error}`);
      }).finally(() => setLoading(false));
  };
  const prepareDataForExcel = data => {
    return data.map((item, index) => {
      const {
        firmShumaID,
        createTime,
        shumaStatusTypeName,
        customers,
        cityName, streetName, houseNumber,
        gush, helka, ttHelka,
        shumaTypeName, originTypeName,
        propertyTypeName,
        shumaTypeID,
        originTypeID,
        estimatePriceFinalRounded,
        finalMarketPrice,
        propertyPriceMinusDepreciation,
        estimatePriceFinal,
        purchaseCompensation,
        paid, totalPayment,
        monthlyRent,
        registeredArea,
        roomsCount,
        floor,
        elevator,
        userFirstName, userLastName,
        shumaDueDate
      } = item;
      const address = [
        [cityName, streetName].filter(Boolean).join(', '),
        houseNumber
      ].filter(Boolean).join(' - ');
      const purchaseCompensationValue = purchaseCompensation ?
        Math.floor(parseFloat(purchaseCompensation)).toLocaleString()
        : '';
      const getCustomersData = () => {
        return Array.isArray(customers)
          ? customers.map(item => [
            [item?.firstName, item?.lastName,].filter(Boolean).join(' '),
            item?.idNumber,
            item?.phone,
            item?.email,
          ].filter(Boolean).join(', ')).filter(Boolean).join('; ')
          : ''
      };

      return {
        [columnNamesInspections[0]]: index + 1,
        [columnNamesInspections[1]]: firmShumaID || '',
        [columnNamesInspections[2]]: createTime ? moment(createTime).format('DD-MM-YYYY') : '',
        [columnNamesInspections[3]]: shumaStatusTypeName || '',
        [columnNamesInspections[4]]: getCustomersData(),
        [columnNamesInspections[5]]: address,
        [columnNamesInspections[6]]: [gush, helka, ttHelka].filter(Boolean).join('/'),
        [columnNamesInspections[7]]: [shumaTypeName, originTypeName].filter(Boolean).join(' - '),
        [columnNamesInspections[8]]: propertyTypeName || '',
        [columnNamesInspections[9]]: getEstimatePrice(
          shumaTypeID,
          originTypeID,
          estimatePriceFinalRounded,
          finalMarketPrice,
          propertyPriceMinusDepreciation,
          estimatePriceFinal
        ).toLocaleString(),
        [columnNamesInspections[10]]: purchaseCompensationValue,
        [columnNamesInspections[11]]: getPaidPartValue(paid, totalPayment).paidPartPercent,
        [columnNamesInspections[12]]: monthlyRent ? monthlyRent.toFixed(2) : '',
        [columnNamesInspections[13]]: registeredArea || '',
        [columnNamesInspections[14]]: roomsCount || '',
        [columnNamesInspections[15]]: floor || '',
        [columnNamesInspections[16]]: getElevator(elevator),
        [columnNamesInspections[17]]: [userFirstName, userLastName].filter(Boolean).join(' '),
        [columnNamesInspections[18]]: shumaDueDate ? moment(shumaDueDate).format('DD-MM-YYYY') : '',
      };
    });
  };

  return (
    <Formik
      initialValues={{
        cityID,
        gush,
        helka,
        shumaID,
        shumaTypeID,
        propertyTypeID,
        responsibleUserID,
        streetID,
        toDate,
        fromDate,
        statusTypeID,
        elevator,
        fromRoomCount,
        toRoomCount,
        fromRegisteredArea,
        toRegisteredArea,
        customerName,
        fromShumaDueDate,
        toShumaDueDate
      }}
      onSubmit={(values) => applyFilter(values, inspectionsProps)}
      enableReinitialize={true}
    >
      {({values, handleBlur, handleSubmit, setFieldValue}) => (
        <form onSubmit={handleSubmit} className={styles.container}>
          <div className={styles.filtersContainer}>
            <ButtonDD
              menuPosition={'left'}
              label={<img src={toAbsoluteUrl('/media/common/Dots_primary.svg')} alt="dots"/>}
              items={[
                {
                  itemContent:
                    (
                      <div className={customStyles.filtersItem}>
                        <div className={styles.filter}>
                          <FilterDatePicker
                            className="px-4"
                            placeholder={formatMessage({id: `${prefix}-fromDate`, defaultMessage: 'From date'})}
                            value={fromDate}
                            onChange={date => {
                              setFieldValue('fromDate', date);
                              handleSubmit();
                            }}
                            maxDate={toDate}
                            label={fromDate
                              ? formatMessage({id: `${prefix}-fromDate`, defaultMessage: 'From date'})
                              : null
                            }
                          />
                          <ClearIcon
                            onClick={() => {
                              setFieldValue('fromDate', '');
                              handleSubmit();
                            }}
                          />
                        </div>
                        <div className={styles.filter}>
                          <FilterDatePicker
                            className="px-4"
                            placeholder={formatMessage({id: `${prefix}-toDate`, defaultMessage: 'To date'})}
                            value={toDate}
                            onChange={date => {
                              setFieldValue('toDate', date);
                              handleSubmit();
                            }}
                            minDate={fromDate}
                            label={toDate
                              ? formatMessage({id: `${prefix}-toDate`, defaultMessage: 'To date'})
                              : null
                            }
                          />
                          <ClearIcon
                            onClick={() => {
                              setFieldValue('toDate', '');
                              handleSubmit();
                            }}
                          />
                        </div>
                        <div className={styles.filter}>
                          <FilterDatePicker
                            className="px-4"
                            placeholder={formatMessage({
                              id: `${prefix}-fromDueDate`,
                              defaultMessage: 'From due date'
                            })}
                            value={fromShumaDueDate}
                            onChange={date => {
                              setFieldValue('fromShumaDueDate', date);
                              handleSubmit();
                            }}
                            maxDate={toShumaDueDate}
                            label={fromShumaDueDate
                              ? formatMessage({id: `${prefix}-fromDueDate`, defaultMessage: 'From due date'})
                              : null
                            }
                          />
                          <ClearIcon
                            onClick={() => {
                              setFieldValue('fromShumaDueDate', '');
                              handleSubmit();
                            }}
                          />
                        </div>
                        <div className={styles.filter}>
                          <FilterDatePicker
                            className="px-4"
                            placeholder={formatMessage({id: `${prefix}-toDueDate`, defaultMessage: 'To due date'})}
                            value={toShumaDueDate}
                            onChange={date => {
                              setFieldValue('toShumaDueDate', date);
                              handleSubmit();
                            }}
                            minDate={fromShumaDueDate}
                            label={toShumaDueDate
                              ? formatMessage({id: `${prefix}-toDueDate`, defaultMessage: 'To due date'})
                              : null
                            }
                          />
                          <ClearIcon
                            onClick={() => {
                              setFieldValue('toShumaDueDate', '');
                              handleSubmit();
                            }}
                          />
                        </div>
                      </div>
                    ),
                },
                {
                  itemContent:
                    (
                      <div className={customStyles.filtersItem}>
                        <FilterText
                          fieldName={'fromRoomCount'}
                          value={values.fromRoomCount}
                          placeholder={formatMessage({
                            id: `${prefix}-fromRoomCount`,
                            defaultMessage: 'From room count'
                          })}
                          containerClassnames={styles.filter}
                          handleBlur={handleBlur}
                          setFieldValue={setFieldValue}
                          handleSubmit={handleSubmit}
                        />
                        <FilterText
                          fieldName={'toRoomCount'}
                          value={values.toRoomCount}
                          placeholder={formatMessage({
                            id: `${prefix}-toRoomCount`,
                            defaultMessage: 'To room count'
                          })}
                          containerClassnames={styles.filter}
                          handleBlur={handleBlur}
                          setFieldValue={setFieldValue}
                          handleSubmit={handleSubmit}
                        />
                        <FilterText
                          fieldName={'fromRegisteredArea'}
                          value={values.fromRegisteredArea}
                          placeholder={formatMessage({
                            id: `${prefix}-fromRegisteredArea`,
                            defaultMessage: 'From reg area'
                          })}
                          containerClassnames={styles.filter}
                          handleBlur={handleBlur}
                          setFieldValue={setFieldValue}
                          handleSubmit={handleSubmit}
                        />
                        <FilterText
                          fieldName={'toRegisteredArea'}
                          value={values.toRegisteredArea}
                          placeholder={formatMessage({
                            id: `${prefix}-toRegisteredArea`,
                            defaultMessage: 'To reg area'
                          })}
                          containerClassnames={styles.filter}
                          handleBlur={handleBlur}
                          setFieldValue={setFieldValue}
                          handleSubmit={handleSubmit}
                        />
                      </div>
                    ),
                },
                {
                  itemContent:
                    (
                      <div className={customStyles.filtersItem}>
                        <FilterText
                          fieldName={'customerName'}
                          value={values.customerName}
                          placeholder={formatMessage({
                            id: `${prefix}-customerName`,
                            defaultMessage: 'Customer name'
                          })}
                          containerClassnames={styles.filter}
                          handleBlur={handleBlur}
                          setFieldValue={setFieldValue}
                          handleSubmit={handleSubmit}
                        />
                        <FilterSelect
                          fieldName={'propertyTypeID'}
                          valueName={values.propertyTypeID}
                          options={propertyTypes}
                          placeholder={formatMessage({
                            id: `${prefix}-propertyType`,
                            defaultMessage: 'Property type'
                          })}
                          containerClassnames={styles.filter}
                          handleBlur={handleBlur}
                          setFieldValue={setFieldValue}
                          handleSubmit={handleSubmit}
                        />
                        <FilterSelect
                          fieldName={'elevator'}
                          valueName={values.elevator}
                          options={elevatorPresence}
                          placeholder={formatMessage({id: `${prefix}-elevator`, defaultMessage: 'Elevator'})}
                          containerClassnames={styles.filter}
                          handleBlur={handleBlur}
                          setFieldValue={setFieldValue}
                          handleSubmit={handleSubmit}
                        />
                      </div>
                    )
                }
              ]}
            />
            <FilterSelect
              fieldName={'cityID'}
              valueName={values.cityID}
              options={cities}
              placeholder={formatMessage({id: `${prefix}-city`, defaultMessage: 'City'})}
              containerClassnames={styles.filter}
              handleBlur={handleBlur}
              setFieldValue={setFieldValue}
              handleSubmit={handleSubmit}
              onChange={({value}) => {
                setFieldValue('cityID', value);
                setFieldValue('streetID', '');
                handleSubmit();
                handleCityChange(values)({value});
              }}
              onClear={() => {
                setFieldValue('cityID', '');
                setFieldValue('streetID', '');
                handleSubmit();
              }}
            />
            <FilterSelect
              fieldName={'streetID'}
              valueName={values.streetID}
              options={streets || []}
              placeholder={formatMessage({id: `${prefix}-street`, defaultMessage: 'Street'})}
              containerClassnames={styles.filter}
              disabled={!values.cityID}
              handleBlur={handleBlur}
              setFieldValue={setFieldValue}
              handleSubmit={handleSubmit}
            />
            <FilterText
              fieldName={'gush'}
              value={values.gush}
              placeholder={formatMessage({id: `${prefix}-gush`, defaultMessage: 'Gush'})}
              containerClassnames={styles.filter}
              handleBlur={handleBlur}
              setFieldValue={setFieldValue}
              handleSubmit={handleSubmit}
            />
            <FilterText
              fieldName={'helka'}
              value={values.helka}
              placeholder={formatMessage({id: `${prefix}-helka`, defaultMessage: 'Helka'})}
              containerClassnames={styles.filter}
              handleBlur={handleBlur}
              setFieldValue={setFieldValue}
              handleSubmit={handleSubmit}
            />
            <FilterText
              fieldName={'shumaID'}
              value={values.shumaID}
              placeholder={formatMessage({id: `${prefix}-shumaID`, defaultMessage: 'Shuma'})}
              containerClassnames={styles.filter}
              handleBlur={handleBlur}
              setFieldValue={setFieldValue}
              handleSubmit={handleSubmit}
            />
            <FilterSelect
              fieldName={'shumaTypeID'}
              valueName={values.shumaTypeID}
              options={shumaTypes}
              placeholder={formatMessage({id: `${prefix}-shumaType`, defaultMessage: 'Shuma type'})}
              containerClassnames={styles.filter}
              handleBlur={handleBlur}
              setFieldValue={setFieldValue}
              handleSubmit={handleSubmit}
            />
            <FilterSelect
              fieldName={'statusTypeID'}
              valueName={values.statusTypeID}
              options={shumaStatuses}
              placeholder={formatMessage({id: `${prefix}-status`, defaultMessage: 'Status'})}
              containerClassnames={styles.filter}
              handleBlur={handleBlur}
              setFieldValue={setFieldValue}
              handleSubmit={handleSubmit}
            />
            <FilterSelect
              fieldName={'responsibleUserID'}
              valueName={values.responsibleUserID}
              options={users}
              placeholder={formatMessage({id: `${prefix}-user`, defaultMessage: 'User'})}
              containerClassnames={styles.filter}
              handleBlur={handleBlur}
              setFieldValue={setFieldValue}
              handleSubmit={handleSubmit}
            />
          </div>
          <div className={styles.buttonsContainer}>
            <ButtonAction
              variant={'primary'}
              useIcon={'/media/common/Settings_white.svg'}
              avoidDoubleClick={false}
              label={inspectionsContext.columnsMode ? 'עמודות' : 'כללי'}
              onClick={() => inspectionsContext.handleColumnsMode()}
              customClassnames={'w-100px'}
            />
            <ButtonAction
              variant={'warning'}
              useIcon={'/media/common/Eye_white.svg'}
              avoidDoubleClick={false}
              label={inspectionsContext.expandAll ? 'הסתיר' : 'הרחב'}
              onClick={() => inspectionsContext.setExpandAll(prev => !prev)}
              customClassnames={'w-100px mx-4'}
            />
            <ButtonAction
              variant={'success'}
              label={
                <>
                  {loading
                    ? (
                      <div className="spinner-border spinner-border-md m-auto text-light" role="status"/>
                    )
                    : (
                      <>
                        <img
                          src={toAbsoluteUrl('/media/common/Excel_white.svg')}
                          alt="export" className={'mr-2'}
                        />
                        <span>{formatMessage({id: 'button-export-to-excel', defaultMessage: 'Export'})}</span>
                      </>
                    )
                  }
                </>
              }
              onClick={handleExport}
              customClassnames={'w-100px'}
            />
          </div>
        </form>
      )}
    </Formik>
  );
}

export default InspectionsPageFilters;